import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Link } from '@inertiajs/react'
import { Building2, Edit2, Check, X } from 'lucide-react'
import { SharedProps } from '@/types'

type Membership = {
  account_id: string
  account_name: string
  account_slug: string
  role: 'member' | 'admin' | 'owner'
  removed: boolean
}

type BackofficeUserDetailed = {
  id: string
  name: string
  email_address: string
  role: 'member' | 'admin' | 'owner'
  account_id: string
  account_name: string
  account_slug: string
  identity_id: string
  removed: boolean
  all_memberships: Membership[]
  created_at: string
}

type Props = {
  user: BackofficeUserDetailed
}

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, string> = {
    owner: 'bg-purple-100 text-purple-700 border-purple-200',
    admin: 'bg-blue-100 text-blue-700 border-blue-200',
    member: 'bg-secondary text-secondary-foreground',
  }
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium border ${config[role] ?? config.member}`}>
      {role}
    </span>
  )
}

function InlineNameEdit({ userId, name }: { userId: string; name: string }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)

  const save = () => {
    router.patch(`/backoffice/users/${userId}`, { user: { name: draft } }, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    })
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between py-3">
        <div>
          <div className="text-xs text-muted-foreground mb-0.5">Display Name</div>
          <div className="text-sm font-medium">{name}</div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setDraft(name); setEditing(true) }}>
          <Edit2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 py-3">
      <div className="flex-1">
        <div className="text-xs text-muted-foreground mb-0.5">Display Name</div>
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          className="h-7 text-sm"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        />
      </div>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={save}>
        <Check className="w-3.5 h-3.5" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => setEditing(false)}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  )
}

export default function BackofficeUsersShow({ user }: Props) {
  const { auth } = usePage<SharedProps>().props
  const isAdmin = auth.identity?.backoffice_admin ?? false

  return (
    <BackofficeLayout
      title={user.name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Users', href: '/backoffice/users' },
        { label: user.name },
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-muted-foreground">{user.name.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight">{user.name}</h1>
              {user.removed && <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Removed</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">{user.email_address}</p>
          </div>
        </div>

        {/* Editable Details */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium">User Details</h2>
          </div>
          <div className="px-4 divide-y">
            {isAdmin ? (
              <InlineNameEdit userId={user.id} name={user.name} />
            ) : (
              <div className="py-3">
                <div className="text-xs text-muted-foreground mb-0.5">Display Name</div>
                <div className="text-sm font-medium">{user.name}</div>
              </div>
            )}
            <div className="py-3">
              <div className="text-xs text-muted-foreground mb-0.5">Email Address</div>
              <div className="text-sm font-medium font-mono">{user.email_address}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Read-only — managed by Identity system</div>
            </div>
            <div className="py-3">
              <div className="text-xs text-muted-foreground mb-0.5">Password</div>
              <div className="text-sm text-muted-foreground">N/A — app uses magic link authentication</div>
            </div>
            <div className="py-3">
              <div className="text-xs text-muted-foreground mb-0.5">Member Since</div>
              <div className="text-sm font-medium">
                {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>
        </section>

        {/* Workspace Memberships */}
        <section className="border rounded-lg">
          <div className="flex items-center gap-2 px-4 py-3 border-b">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-medium">Workspace Memberships ({user.all_memberships.length})</h2>
          </div>
          {user.all_memberships.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">No workspace memberships</div>
          ) : (
            <div className="divide-y">
              {user.all_memberships.map(m => (
                <Link
                  key={m.account_id}
                  href={`/backoffice/accounts/${m.account_id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${m.removed ? 'opacity-50' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{m.account_name}</span>
                      <RoleBadge role={m.role} />
                      {m.removed && <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Removed</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">/{m.account_slug}</div>
                  </div>
                  <span className="text-xs text-muted-foreground">→</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </BackofficeLayout>
  )
}
